import type { Layout } from '../../data/types';
import type { PolygonWithHoles, Ring, Vec2 } from '../../geo/types';
import type { Obstacle } from '../types';
import { fromRing, inflate } from '../../geo/clip';
import { distancePointSegment, pointInPolygon, ringBounds } from '../../geo/polygon';
import { createPolygonIndex } from '../../geo/polygonIndex';
import { createSpatialHash } from '../../geo/spatialHash';
import { windmillSweepFootprint } from './windmills';
import { windmillSetting } from './wallMills';
import { TREE_CROWN_REACH_M, TREE_TRUNK_REACH_M } from './trees';

type Windmill = Layout['windmills'][number];
type Landmark = Layout['landmarks'][number];

export interface KeepOut {
  contains(p: Vec2): boolean;
}

const INDEX_CELL_M = 50;
const SOLID_CELL_M = 25;

function nearRing(p: Vec2, ring: Ring, reach: number): boolean {
  return ring.some(
    (a, i) => distancePointSegment(p, { a, b: ring[(i + 1) % ring.length]! }) < reach
  );
}

/*
 * Every point within `reach` of a solid, measured exactly against the few solids a spatial hash
 * returns: a city holds thousands of houses, and unioning their offsets takes seconds.
 */
function solidsKeepOut(solids: readonly PolygonWithHoles[], reach: number): KeepOut {
  const hash = createSpatialHash<PolygonWithHoles>(SOLID_CELL_M);
  for (const solid of solids) {
    const b = ringBounds(solid.outer);
    const grown = {
      minX: b.minX - reach,
      minY: b.minY - reach,
      maxX: b.maxX + reach,
      maxY: b.maxY + reach,
    };
    hash.insert(solid, grown);
  }
  const reaches = (p: Vec2, solid: PolygonWithHoles) =>
    pointInPolygon(p, solid) || [solid.outer, ...solid.holes].some((r) => nearRing(p, r, reach));
  return {
    contains: (p) =>
      hash.query({ minX: p[0], minY: p[1], maxX: p[0], maxY: p[1] }).some((s) => reaches(p, s)),
  };
}

/*
 * Where no tree may stand: every building and mill base, landmark footprint and sail disc,
 * grown by the widest crown so that no crown reaches into a wall, a roof or a turning sail.
 */
export function vegetationKeepOut(
  landmarks: readonly Landmark[],
  obstacles: readonly Obstacle[],
  windmills: readonly Windmill[]
): KeepOut {
  const rings: readonly Ring[] = [
    ...obstacles.map((o) => o.polygon),
    ...landmarks.map((l) => l.footprint),
    ...windmills.map((w) => windmillSweepFootprint(w.pos, w.heading, w.kind, windmillSetting(w))),
  ];
  return crownKeepOut(rings.map(fromRing));
}

/* Every point within the widest crown's reach of the given solids, so no crown enters one. */
export function crownKeepOut(solids: readonly PolygonWithHoles[]): KeepOut {
  return solidsKeepOut(solids, TREE_CROWN_REACH_M);
}

/* Roadside trees may lean their crowns over a street, road or dike, but no trunk stands on one. */
export function roadKeepOut(streets: readonly PolygonWithHoles[]): KeepOut {
  return createPolygonIndex(inflate(streets, TREE_TRUNK_REACH_M, 'round'), INDEX_CELL_M);
}
